import axios from 'axios';
import i18n from 'i18next';
import I18nextBrowserLanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import { postReportMissingConst } from '../apiservice/apiService';

// Track reported keys to avoid duplicate API calls
const reportedKeys = new Set();

// Flag to track when locale resources are fully loaded
let localeResourcesLoaded = false;

// Function to check if locale resources are loaded
export const isLocaleResourcesLoaded = () => localeResourcesLoaded;

// Function to check if key is in SCREAMING_SNAKE_CASE format (uppercase, no spaces)
const isValidKeyFormat = key => {
  // Only report keys that are uppercase letters, numbers, and underscores (no spaces, no lowercase)
  return /^[A-Z0-9_]+$/.test(key);
};

// Function to report missing translation key to API
const reportMissingKey = async (key, lng) => {
  // Only report keys that match SCREAMING_SNAKE_CASE format
  if (!isValidKeyFormat(key)) return;

  if (reportedKeys.has(key)) return;
  reportedKeys.add(key);

  // Get uuid from URL params (device ID)
  // If multiple comma-separated UUIDs are provided, use only the first one
  const urlParams = new URLSearchParams(window.location.search);
  const rawUuid = urlParams?.get('uuid') || '';
  const uuid = rawUuid?.split(',')?.[0]?.trim() || '';

  // Get environment name
  const env = import.meta.env.VITE_NODE_ENV || 'development';

  const payload = {
    const: key,
    description: 'This constant is missing from the translation table',
    di: uuid,
    env: env,
  };

  try {
    await postReportMissingConst(payload);
    console.warn(`Reported missing translation key: [${lng}] ${key}`);
  } catch (error) {
    console.error(`Failed to report missing key ${key}:`, error);
  }
};

const loadLocaleResources = async locale => {
  const resources = { translation: {} };
  const baseUrl = '/locale/';
  const timestamp = Date.now();

  // Cache-busting headers to always fetch fresh data
  const noCacheHeaders = {
    headers: {
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      Pragma: 'no-cache',
      Expires: '0',
    },
  };

  // Fetch default locale file (English as fallback) FIRST
  // This provides base translations that locale-specific file will override
  try {
    const defaultResponse = await axios.get(
      `${baseUrl}locale.json?t=${timestamp}`,
      noCacheHeaders
    );
    Object.assign(resources.translation, defaultResponse.data);
  } catch {
    console.log('Could not load default locale file');
  }

  // Fetch locale-specific file SECOND (overrides fallback with locale-specific translations)
  try {
    const localeResponse = await axios.get(
      `${baseUrl}locale_${locale}.json?t=${timestamp}`,
      noCacheHeaders
    );
    Object.assign(resources.translation, localeResponse.data);
  } catch {
    console.debug(`Could not load ${locale} translation file`);
  }

  return { [locale]: resources };
};

const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.languages?.[0] || 'en';
  const shortLang = browserLang.split('-')[0];
  return shortLang;
};

try {
  localStorage.removeItem('i18nextLng');
  document.cookie = 'i18next=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
  document.cookie =
    'i18nextLng=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
} catch (e) {
  console.log('Could not clear i18next cache:', e);
}

const initResources = async () => {
  try {
    const browserLang = getBrowserLanguage();
    const supportedLngs = [
      'en',
      'ko',
      'zh',
      'ja',
      'de',
      'fr',
      'es',
      'it',
      'pt',
      'ru',
      'tr',
      'pl',
      'cs',
      'sk',
      'th',
      'vi',
      'id',
      'in',
      'ar',
      'he',
      'iw',
      'el',
    ];
    const detectedLng = supportedLngs.includes(browserLang)
      ? browserLang
      : 'en';

    const resources = await loadLocaleResources(detectedLng);

    Object.keys(resources).forEach(locale => {
      i18n.addResourceBundle(
        locale,
        'translation',
        resources[locale].translation,
        true,
        true
      );
    });

    i18n.changeLanguage(detectedLng);
    localeResourcesLoaded = true;
    console.log('Locale resources loaded successfully');
  } catch (error) {
    console.warn('Failed to load initial language resources:', error);
    // Still mark as loaded (even if failed) to prevent infinite loading
    localeResourcesLoaded = true;
  }
};

i18n
  .use(I18nextBrowserLanguageDetector)
  .use(initReactI18next)
  .init(
    {
      fallbackLng: 'en',
      saveMissing: true,
      supportedLngs: [
        'en',
        'ko',
        'zh',
        'de',
        'fr',
        'es',
        'it',
        'pt',
        'ru',
        'tr',
        'pl',
        'cs',
        'sk',
        'th',
        'vi',
        'id',
        'in',
        'ar',
        'he',
        'iw',
        'el',
      ],
      ns: ['translation'],
      defaultNS: 'translation',
      interpolation: {
        escapeValue: false,
      },
      react: {
        useSuspense: false,
      },
      detection: {
        order: ['navigator', 'htmlTag'],
        caches: [],
        lookupCookie: false,
        cookieMinutes: 0,
      },
      missingKeyHandler: (lng, ns, key, fallbackValue) => {
        console.log('missing  const ', lng, ns, key, fallbackValue);
        // Skip reporting keys that end with an underscore
        if (
          key &&
          fallbackValue &&
          fallbackValue === key &&
          !key.endsWith('_') &&
          !key.toLowerCase().endsWith('_unknown')
        ) {
          reportMissingKey(key, lng);
        }
        return fallbackValue || key;
      },
    },
    err => {
      if (err) {
        console.error('i18n initialization error:', err);
      }
      initResources();
    }
  );

export default i18n;
